import {
    listSelectableSowCreditPools,
    type SowCreditPoolType,
} from '@wevo/wevo-access/sow-credit-pools';

export type AllocationAvailabilityMode = 'all_upfront' | 'periodic_release';
export type AllocationCadence = 'monthly' | 'quarterly' | 'yearly';
export type AllocationUnusedBehavior = 'carry_forward' | 'expire_end_of_period';

export type AllocationRow = {
    key: string;
    poolType: SowCreditPoolType;
    initialAmount: string;
    availabilityMode: AllocationAvailabilityMode;
    cadence: AllocationCadence;
    releaseAmount: string;
    anchorDay: string;
    anchorDayManuallyEdited: boolean;
    unusedBehavior: AllocationUnusedBehavior;
};

export type SowPoolPayload = {
    poolType: SowCreditPoolType;
    initialAmount: string;
    resetPolicy: Record<string, unknown>;
};

function newRowKey(): string {
    return crypto.randomUUID();
}

export function anchorDayFromStartDate(startDate: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate.trim());
    if (!match) {
        return '1';
    }
    const day = Number(match[3]);
    return Number.isInteger(day) && day >= 1 && day <= 31 ? String(day) : '1';
}

export function createAllocationRow(poolType: SowCreditPoolType, startDate = ''): AllocationRow {
    return {
        key: newRowKey(),
        poolType,
        initialAmount: '',
        availabilityMode: 'all_upfront',
        cadence: 'monthly',
        releaseAmount: '',
        anchorDay: anchorDayFromStartDate(startDate),
        anchorDayManuallyEdited: false,
        unusedBehavior: 'carry_forward',
    };
}

export function getSelectableCreditPoolOptions(excludedPoolTypes: Iterable<string> = []) {
    const excluded = new Set(excludedPoolTypes);
    return listSelectableSowCreditPools('credit')
        .filter((pool) => !excluded.has(pool.type))
        .map((pool) => ({
            value: pool.type,
            label: pool.label,
        }));
}

export function allocationRowsToPoolsPayload(
    allocations: AllocationRow[],
    unitMode: 'credit' | 'token',
): SowPoolPayload[] {
    return allocations
        .map((row) => ({
            poolType: unitMode === 'token' ? 'token' : row.poolType,
            initialAmount: row.initialAmount.trim(),
            resetPolicy:
                unitMode === 'token' || row.availabilityMode === 'all_upfront'
                    ? { type: 'none' as const }
                    : {
                          type: 'periodic_release' as const,
                          cadence: row.cadence,
                          releaseAmount: Number(row.releaseAmount.trim()),
                          anchorDay: Number(row.anchorDay.trim()),
                          unusedBehavior: row.unusedBehavior,
                      },
        }))
        .filter((row) => row.initialAmount !== '');
}

export function poolsPayloadIsValid(poolsPayload: SowPoolPayload[]): boolean {
    return (
        poolsPayload.length > 0 &&
        poolsPayload.every((row) => {
            if (!Number.isFinite(Number(row.initialAmount))) {
                return false;
            }
            if (row.resetPolicy.type === 'none') {
                return true;
            }

            const releaseAmount = Number(row.resetPolicy.releaseAmount);
            const anchorDay = Number(row.resetPolicy.anchorDay);
            return (
                Number.isFinite(releaseAmount) &&
                releaseAmount > 0 &&
                Number.isInteger(anchorDay) &&
                anchorDay >= 1 &&
                anchorDay <= 31
            );
        })
    );
}

function formatCreditAmount(value: number): string {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}

function unusedBehaviorSummaryLabel(unusedBehavior: AllocationUnusedBehavior): string {
    switch (unusedBehavior) {
        case 'carry_forward':
            return 'Credits carry';
        case 'expire_end_of_period':
            return 'Credits expire';
        default: {
            const exhaustiveCheck: never = unusedBehavior;
            return exhaustiveCheck;
        }
    }
}

function normalizePeriodicReleasePolicy(resetPolicy: Record<string, unknown>): {
    cadence: AllocationCadence;
    releaseAmount: number;
    anchorDay: number;
    unusedBehavior: AllocationUnusedBehavior;
} | null {
    if (resetPolicy.type !== 'periodic_release') {
        return null;
    }

    const cadence = resetPolicy.cadence;
    const releaseAmount = Number(resetPolicy.releaseAmount);
    const anchorDay = Number(resetPolicy.anchorDay);
    const unusedBehavior = resetPolicy.unusedBehavior;

    if (
        (cadence !== 'monthly' && cadence !== 'quarterly' && cadence !== 'yearly') ||
        !Number.isFinite(releaseAmount) ||
        releaseAmount <= 0 ||
        !Number.isInteger(anchorDay) ||
        anchorDay < 1 ||
        anchorDay > 31 ||
        (unusedBehavior !== 'carry_forward' && unusedBehavior !== 'expire_end_of_period')
    ) {
        return null;
    }

    return {
        cadence,
        releaseAmount,
        anchorDay,
        unusedBehavior,
    };
}

export function describePeriodicReleaseScheduleSummary(
    resetPolicy: Record<string, unknown> | undefined,
): string | null {
    if (!resetPolicy) {
        return null;
    }

    const policy = normalizePeriodicReleasePolicy(resetPolicy);
    if (!policy) {
        return null;
    }

    return [
        `Cadence: ${policy.cadence}`,
        `Anchor day: ${policy.anchorDay}`,
        `Credits released: ${formatCreditAmount(policy.releaseAmount)}`,
        unusedBehaviorSummaryLabel(policy.unusedBehavior),
    ].join(', ');
}

export function describeAllocationResetPolicy(
    resetPolicy: Record<string, unknown> | undefined,
): string {
    if (!resetPolicy || resetPolicy.type !== 'periodic_release') {
        return 'All upfront';
    }

    const cadence =
        typeof resetPolicy.cadence === 'string' && resetPolicy.cadence.trim() !== ''
            ? resetPolicy.cadence
            : 'periodic';
    const releaseAmount =
        typeof resetPolicy.releaseAmount === 'number' ||
        typeof resetPolicy.releaseAmount === 'string'
            ? String(resetPolicy.releaseAmount)
            : '—';
    return `Periodic release (${cadence}, ${releaseAmount} per period)`;
}
